// Third-party OAuth authorization APIs
import axios from 'axios';
import instance from '../index';
import { thirdPartyOauth } from '../interface';

// Keep this host independent from MCP OAuth so the third-party API can be changed separately later.
const THIRD_PARTY_OAUTH_HOST = String(
  ENV_DATA.VITE_THIRD_PARTY_OAUTH_HOST || ENV_DATA.VITE_MCP_OAUTH_HOST || ''
).replace(/\/+$/, '');
const THIRD_PARTY_OAUTH_AUTHORIZE_URL = `${THIRD_PARTY_OAUTH_HOST}/oauth/authorize/complete`;

/** 绑定类接口的外部平台标识 */
export const WORKBUDDY_PLATFORM_CODE = 'workbuddy';

// The authorization completion endpoint does not use the jmHttp gateway headers or cookies.
const oauthClient = axios.create();

/**
 * Submit third-party OAuth authorization.
 *
 * @param {URLSearchParams|string} body - Serialized authorization form data
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const authorizeThirdPartyOauth = (body) => {
  return oauthClient.post(THIRD_PARTY_OAUTH_AUTHORIZE_URL, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    withCredentials: false
  });
};

/**
 * Exchange a WorkBuddy authorization code for the binding state.
 *
 * 已绑定时下发 comein 会话（token + uid），未绑定时不报错，只回 bound=false 与一次性
 * bindTicket。无论绑定与否，传入的 code 都已被消费，重试必须重新调用 wb.login 取新 code。
 * @param {string} code - Authorization code returned by wb.login
 * @returns {Promise<object>} Binding state and Comein session response body
 */
export const loginWorkBuddyBinding = (code) => {
  return instance.post(
    thirdPartyOauth.bindingLogin,
    {
      platform_code: WORKBUDDY_PLATFORM_CODE,
      code
    },
    {
      bodyType: 'form',
      hideMessage: true,
      // 未绑定属于正常分支，由调用方决定是否引导去授权页，不走全局登录失效拦截。
      skipOauthLoginRedirect: true
    }
  );
};

/**
 * Bind the WorkBuddy account to the logged-in Comein account.
 *
 * @param {object} params - Binding request parameters
 * @param {string} [params.bindTicket] - Binding ticket returned by binding/login，已绑定账号不下发
 * @param {string} [params.loginToken] - Current Comein login token
 * @param {string|number} [params.uid] - Current Comein user ID
 * @returns {Promise<object>} Binding response body
 */
export const bindWorkBuddyAccount = ({ bindTicket, loginToken, uid } = {}) => {
  const data = { platform_code: WORKBUDDY_PLATFORM_CODE };
  // 绑定票据仅未绑定账号才有，已绑定账号凭 comein 会话直接确认授权。
  if (bindTicket) data.bind_ticket = bindTicket;
  // 登录 token 可选，存在时用于绑定当前进门账号。
  if (loginToken) data.login_token = loginToken;
  // 用户 ID 可选，存在时随绑定请求传递给后端。
  if (uid) data.uid = uid;

  return instance.post(thirdPartyOauth.bindingBind, data, {
    bodyType: 'form',
    hideMessage: true,
    skipOauthLoginRedirect: true
  });
};

/**
 * Remove the binding between the WorkBuddy account and the logged-in Comein account.
 *
 * 后端从登录态取 comein 用户，从 code 取 WorkBuddy 用户，因此只需传平台标识与授权码。
 * @param {string} code - Authorization code returned by wb.login
 * @returns {Promise<object>} Unbinding response body
 */
export const unbindWorkBuddyAccount = (code) => {
  return instance.post(
    thirdPartyOauth.bindingUnbind,
    {
      platform_code: WORKBUDDY_PLATFORM_CODE,
      code
    },
    {
      bodyType: 'form',
      hideMessage: true,
      skipOauthLoginRedirect: true
    }
  );
};
